import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { User, Save, StickyNote, ScrollText, BarChart3, Trash2, Shield, Plus, Euro, Briefcase, KeyRound } from "lucide-react";
import api from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { WachtwoordWijzigenDialog } from "@/components/WachtwoordWijzigenDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/pages/Kennisbank";
import { toast } from "sonner";

const ROL_LABEL = { werknemer: "Werknemer", "or-lid": "OR-lid", beheerder: "Beheerder", superbeheerder: "Superbeheerder" };

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4">
    <p className="font-heading text-2xl font-bold text-slate-900">{value}</p>
    <p className="text-xs text-slate-500 mt-1">{label}</p>
  </div>
);

const formatDatum = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return null; }
};

const MijnOmgeving = () => {
  const { user, refresh, authEnabled } = useUser();
  const [searchParams] = useSearchParams();
  const actieveTab = searchParams.get("tab") === "privacy" ? "privacy" : "checks";
  const [wwDialoog, setWwDialoog] = useState(false);
  const [checks, setChecks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [audit, setAudit] = useState([]);
  const [stats, setStats] = useState(null);
  const [titel, setTitel] = useState("");
  const [inhoud, setInhoud] = useState("");

  const loadAll = () => {
    api.get("/checks").then((r) => setChecks(r.data));
    api.get("/notes").then((r) => setNotes(r.data));
    api.get("/audit").then((r) => setAudit(r.data));
    api.get("/stats").then((r) => setStats(r.data));
  };
  useEffect(() => { loadAll(); }, [user?.id]);

  const addNote = async () => {
    if (!titel.trim() || !inhoud.trim()) { toast.error("Vul titel en inhoud in."); return; }
    await api.post("/notes", { titel, inhoud });
    setTitel(""); setInhoud("");
    api.get("/notes").then((r) => setNotes(r.data));
    toast.success("Notitie opgeslagen.");
  };

  const delNote = async (id) => { await api.delete(`/notes/${id}`); api.get("/notes").then((r) => setNotes(r.data)); };
  const delCheck = async (id) => { await api.delete(`/checks/${id}`); api.get("/checks").then((r) => setChecks(r.data)); };

  const deleteData = async () => {
    const res = await api.delete("/me/data");
    toast.success(res.data.melding);
    loadAll();
  };

  const deleteAccount = async () => {
    try {
      const res = await api.delete("/me/account");
      toast.success(res.data.melding);
      localStorage.removeItem("zk_token");
      setTimeout(() => { window.location.href = "/"; }, 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Account verwijderen is niet gelukt.");
    }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Mijn omgeving" icon={User}
        accent={{ chip: "bg-rose-600", bar: "from-rose-400 to-rose-700", text: "text-rose-600" }}
        subtitle="Je persoonlijke, afgeschermde omgeving. Alleen jij hebt toegang tot je checks, notities en auditlogs." />

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center font-heading font-bold shrink-0">
            {(user?.naam || user?.email || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900">{user?.naam || user?.email?.split("@")[0] || "Gebruiker"}</p>
            <p className="text-sm text-slate-500 break-words">{user?.email} · {ROL_LABEL[user?.rol] || "Werknemer"}</p>
          </div>
          <span className="sm:ml-auto text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 flex items-center gap-1 shrink-0">
            <Shield className="w-3 h-3" /> AVG-toestemming: {user?.avg_toestemming ? "gegeven" : "nee"}
          </span>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div data-testid="account-lid-sinds">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Lid sinds</p>
            <p className="text-sm font-medium text-slate-800 mt-0.5">{formatDatum(user?.created_at) || "–"}</p>
          </div>
          <div data-testid="account-aantal-vragen">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Gestelde vragen</p>
            <p className="text-sm font-medium text-slate-800 mt-0.5">{audit.length}</p>
          </div>
          <div data-testid="account-laatste-activiteit">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Laatste activiteit</p>
            <p className="text-sm font-medium text-slate-800 mt-0.5">
              {audit.length > 0 ? new Date(audit[0].datumtijd).toLocaleString("nl-NL") : "Nog geen activiteit"}
            </p>
          </div>
        </div>
        {authEnabled && user?.auth_provider !== "google" && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <Button data-testid="mijn-wachtwoord-wijzigen" variant="outline" size="sm"
              onClick={() => setWwDialoog(true)} className="border-slate-300 text-slate-700">
              <KeyRound className="w-4 h-4 mr-1.5 text-[#B9772B]" /> Wachtwoord wijzigen
            </Button>
          </div>
        )}
      </div>
      <WachtwoordWijzigenDialog open={wwDialoog} onOpenChange={setWwDialoog} />

      <Tabs key={actieveTab} defaultValue={actieveTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="checks" data-testid="tab-checks"><Save className="w-4 h-4 mr-1.5" /> Opgeslagen checks</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes"><StickyNote className="w-4 h-4 mr-1.5" /> Notities</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="w-4 h-4 mr-1.5" /> Auditlog</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats"><BarChart3 className="w-4 h-4 mr-1.5" /> Statistieken</TabsTrigger>
          <TabsTrigger value="privacy" data-testid="tab-privacy"><Shield className="w-4 h-4 mr-1.5" /> Privacy</TabsTrigger>
        </TabsList>

        <TabsContent value="checks" className="mt-4 space-y-3">
          {checks.length === 0 && <p className="text-slate-400 text-sm">Nog geen opgeslagen checks.</p>}
          {checks.map((c) => (
            <div key={c.id} data-testid={`saved-check-${c.id}`} className="bg-white border border-slate-200 rounded-lg p-4 flex justify-between items-start gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                  {c.type_check === "salarischeck" ? <Euro className="w-4 h-4" /> : <Briefcase className="w-4 h-4" />}
                  {c.type_check === "salarischeck" ? "Salarischeck" : "Functiecheck"}
                </p>
                {c.type_check === "salarischeck" ? (
                  <p className="text-sm text-slate-600 mt-1">Functiegroep {c.uitkomst?.functiegroep}, periodiek {c.uitkomst?.periodiek} → &euro; {c.uitkomst?.berekend_salaris?.toLocaleString("nl-NL")} bruto/mnd</p>
                ) : (
                  <p className="text-sm text-slate-600 mt-1">Indicatie functiegroep {c.uitkomst?.functiegroep} ({c.uitkomst?.schaalindicatie})</p>
                )}
                <p className="text-xs text-slate-400 mt-1">{new Date(c.timestamp).toLocaleString("nl-NL")}</p>
              </div>
              <button data-testid={`delete-check-${c.id}`} onClick={() => delCheck(c.id)} className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="notes" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 space-y-3">
            <div><Label>Titel</Label><Input data-testid="note-titel" value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Notitietitel" /></div>
            <div><Label>Inhoud</Label><Textarea data-testid="note-inhoud" value={inhoud} onChange={(e) => setInhoud(e.target.value)} placeholder="Je persoonlijke notitie..." className="resize-none" /></div>
            <Button data-testid="note-add-btn" onClick={addNote} className="bg-slate-900"><Plus className="w-4 h-4 mr-2" /> Notitie opslaan</Button>
          </div>
          <div className="space-y-3">
            {notes.length === 0 && <p className="text-slate-400 text-sm">Nog geen notities.</p>}
            {notes.map((n) => (
              <div key={n.id} data-testid={`note-${n.id}`} className="bg-white border border-slate-200 rounded-lg p-4 flex justify-between items-start gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{n.titel}</p>
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{n.inhoud}</p>
                  <p className="text-xs text-slate-400 mt-1">{new Date(n.timestamp).toLocaleString("nl-NL")}</p>
                </div>
                <button data-testid={`delete-note-${n.id}`} onClick={() => delNote(n.id)} className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4 space-y-3">
          <p className="text-xs text-slate-500">Je auditlog is uitsluitend voor jou zichtbaar. Per vraag worden bronnen, versies, status, vertrouwensniveau en AI-versie vastgelegd.</p>
          {audit.length === 0 && <p className="text-slate-400 text-sm">Nog geen vragen gesteld.</p>}
          {audit.map((a) => (
            <div key={a.id} data-testid={`audit-${a.id}`} className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex justify-between items-start gap-3">
                <p className="text-sm font-medium text-slate-900">{a.vraagtekst}</p>
                <StatusBadge status={a.status} />
              </div>
              <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                <span>{new Date(a.datumtijd).toLocaleString("nl-NL")}</span>
                <span>Vertrouwen: {a.vertrouwensniveau}</span>
                <span>AI: {a.ai_versie}</span>
                {a.extern_gebruikt && <span className="text-amber-700">Externe bron gebruikt</span>}
                {a.kritisch_onderwerp && <span className="text-amber-700">Kritisch onderwerp</span>}
                {a.wettelijke_voorrang && <span className="text-blue-700">Wettelijke voorrang</span>}
              </div>
              {a.gebruikte_documentversies?.length > 0 && (
                <p className="text-[11px] text-slate-400 mt-1">Bronnen: {a.gebruikte_documentversies.join(" · ")}</p>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="stats" className="mt-4 space-y-6">
          <div>
            <h3 className="font-heading font-semibold text-slate-900 mb-1">Mijn statistieken</h3>
            <p className="text-xs text-slate-500 mb-3">Uitsluitend jouw eigen, aan je account gekoppelde activiteit.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="mijn-statistieken">
              <StatCard label="Gestelde vragen" value={audit.length} />
              <StatCard label="Opgeslagen checks" value={checks.length} />
              <StatCard label="Notities" value={notes.length} />
            </div>
          </div>
          <div className="border-t border-slate-200 pt-5">
            <h3 className="font-heading font-semibold text-slate-900 mb-1">Platformstatistieken (alle gebruikers)</h3>
            <p className="text-xs text-slate-500 mb-3" data-testid="platform-stats-toelichting">
              Deze cijfers hebben betrekking op alle gebruikers van ZoetwarenKompas en niet op jouw persoonlijke activiteit. Anonieme, geaggregeerde gebruiksmetingen voor MVP-validatie; inhoud van notities, wachtwoorden en gevoelige gegevens worden nooit gemeten.
            </p>
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="platform-statistieken">
                <StatCard label="Gestelde vragen" value={stats.aantal_vragen} />
                <StatCard label="Salarischecks" value={stats.aantal_salarischecks} />
                <StatCard label="Functiechecks" value={stats.aantal_functiechecks} />
                <StatCard label="Externe raadplegingen" value={stats.aantal_externe_raadplegingen} />
                <StatCard label="Zoekopdrachten" value={stats.aantal_zoekopdrachten} />
                <StatCard label="Actieve gebruikers" value={stats.aantal_actieve_gebruikers} />
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="privacy" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-slate-700 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900">Volledige controle over je gegevens</p>
                <p className="text-sm text-slate-600 mt-1">Je gegevens zijn per gebruiker afgeschermd. Andere gebruikers – ook OR-leden – kunnen jouw vragen, notities, checks of auditlogs niet inzien. Je kunt al je persoonlijke gegevens op elk moment verwijderen.</p>
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="delete-data-btn" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" /> Verwijder mijn gegevens
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Al je gegevens verwijderen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Dit verwijdert permanent al je vragen (auditlog), opgeslagen checks, notities en feedback. Deze actie kan niet ongedaan worden gemaakt. Bronbestanden en documentversies blijven behouden.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel">Annuleren</AlertDialogCancel>
                  <AlertDialogAction data-testid="delete-confirm" onClick={deleteData} className="bg-red-600 hover:bg-red-700">Definitief verwijderen</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <p className="text-xs text-slate-400">Je account blijft hierbij gewoon bestaan en je kunt blijven inloggen.</p>
          </div>

          <div className="bg-white border border-red-200 rounded-lg p-6 space-y-4 mt-4">
            <div className="flex items-start gap-3">
              <Trash2 className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900">Mijn account verwijderen</p>
                <p className="text-sm text-slate-600 mt-1">
                  Hiermee beëindig je jouw account definitief. Dit is iets anders dan &ldquo;Verwijder mijn gegevens&rdquo; hierboven:
                  naast al je gegevens wordt ook je account zelf verwijderd, worden al je sessies beëindigd en kun je niet meer inloggen.
                  Voor een nieuw account neem je contact op met de beheerder.
                </p>
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="delete-account-btn" variant="outline" className="border-red-400 text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" /> Mijn account verwijderen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Weet je zeker dat je jouw account wilt verwijderen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Deze actie kan niet ongedaan worden gemaakt. Je account, je gegevens en al je sessies worden definitief
                    verwijderd en je kunt daarna niet meer inloggen.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-account-cancel">Annuleren</AlertDialogCancel>
                  <AlertDialogAction data-testid="delete-account-confirm" onClick={deleteAccount} className="bg-red-600 hover:bg-red-700">Definitief verwijderen</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MijnOmgeving;
